import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { Button } from "../components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FAQ } from "../data/faq";

const FAQ_REMOVED_INLINE = null;

const CHAT_MSGS = [
  { u: "Ana", c: "🔥 ¡vamos!", d: "bg-emerald-400" },
  { u: "Leo", c: "GG 👏", d: "bg-cyan-400" },
  { u: "Sam", c: "🎤 ¡mi turno!", d: "bg-amber-400" },
  { u: "Mia", c: "💚 apoyando", d: "bg-pink-400" },
  { u: "Kai", c: "let's go 🚀", d: "bg-violet-400" },
  { u: "Noa", c: "🏆 team A", d: "bg-emerald-400" },
];

function LiveChatOverlay() {
  return (
    <div data-testid="hero-live-overlay"
      className="hidden md:flex flex-col absolute right-[2%] top-[56%] -translate-y-1/2 w-[190px] h-[320px] z-0 opacity-60 pointer-events-none">
      <motion.div
        className="self-end mb-3 inline-flex items-center gap-1.5 rounded-full bg-red-600/90 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-white"
        animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
        <span className="w-1.5 h-1.5 rounded-full bg-white" /> LIVE
      </motion.div>
      <div className="relative flex-1 overflow-hidden"
        style={{ maskImage: "linear-gradient(to bottom, transparent, #000 18%, #000 82%, transparent)", WebkitMaskImage: "linear-gradient(to bottom, transparent, #000 18%, #000 82%, transparent)" }}>
        <motion.div className="flex flex-col gap-2"
          animate={{ y: ["0%", "-50%"] }} transition={{ duration: 15, repeat: Infinity, ease: "linear" }}>
          {[...CHAT_MSGS, ...CHAT_MSGS].map((m, i) => (
            <div key={`${m.u}-${i}`} className="flex items-center gap-2 rounded-xl bg-white/[0.04] px-2.5 py-1.5">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${m.d} opacity-80`} />
              <span className="text-[11px] font-semibold text-white/70 shrink-0">{m.u}</span>
              <span className="text-[11px] text-zinc-400 truncate">{m.c}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [fee, setFee] = useState(30);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 110]);

  useEffect(() => {
    api.get("/config/requirements").then(({ data }) => setFee(data.platform_fee_percent)).catch(() => {});
  }, []);

  const features = [
    { img: "/how-verify.jpg", title: t("landing_f1t"), desc: t("landing_f1d") },
    { img: "/how-split.jpg", title: t("landing_f2t"), desc: t("landing_f2d", { s: 100 - fee, p: fee }) },
    { img: "/how-escrow.jpg", title: t("landing_f3t"), desc: t("landing_f3d") },
  ];

  const faqs = FAQ[lang] || FAQ.en;

  return (
    <div data-testid="landing-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <motion.img style={{ y: heroY }} src="/hero-3d.jpg" alt=""
            className="absolute left-0 -top-[15%] w-full h-[130%] object-cover object-right opacity-70 will-change-transform" />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/40 via-transparent to-zinc-950" />
          <LiveChatOverlay />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-28 sm:py-36">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-block text-xs uppercase tracking-[0.25em] font-bold text-emerald-400 mb-6">{t("landing_badge")}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05]">
              {t("landing_hero1")}<br />
              <span className="text-emerald-400">{t("landing_hero2")}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed">
              {t("landing_sub")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button data-testid="hero-cta-streamer" size="lg" className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white px-8 h-12"
                onClick={() => navigate("/auth?role=streamer")}>
                {t("landing_cta_streamer")} <ArrowRight size={16} className="ml-2" />
              </Button>
              <Button data-testid="hero-cta-fan" size="lg" variant="outline" className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800 hover:text-white px-8 h-12 bg-transparent"
                onClick={() => navigate("/descubrir")}>
                {t("landing_cta_fan")}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="how-it-works" className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-base md:text-lg font-bold text-zinc-500 uppercase tracking-[0.2em] mb-10">{t("landing_how")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              className="group bg-zinc-900 border border-zinc-800/60 rounded-2xl p-6 overflow-hidden">
              <div className="relative rounded-xl overflow-hidden mb-6 bg-zinc-950/40 border border-zinc-800/60">
                <img data-testid="how-3d-img" src={f.img} alt="" className="w-full h-44 object-cover" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">{f.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section data-testid="faq-section" id="faq" className="max-w-3xl mx-auto px-6 pb-20">
        <span className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-400">{t("faq_badge")}</span>
        <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-white mt-2 mb-2">{t("faq_title")}</h2>
        <p className="text-sm text-zinc-400 mb-8">{t("faq_sub")}</p>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((item, i) => (
            <AccordionItem key={item.q} value={`faq-${i}`} data-testid="faq-item" className="border-zinc-800">
              <AccordionTrigger className="text-left text-white hover:text-emerald-400 text-base">{item.q}</AccordionTrigger>
              <AccordionContent className="text-zinc-400 text-sm leading-relaxed">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="bg-gradient-to-r from-emerald-950/70 to-zinc-900 border border-emerald-900/40 rounded-3xl p-10 sm:p-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">{t("landing_cta_title")}</h2>
            <p className="mt-3 text-zinc-400">{t("landing_cta_sub")}</p>
          </div>
          <Button data-testid="footer-cta-btn" size="lg" className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold px-8 h-12 shrink-0"
            onClick={() => navigate("/auth?role=streamer")}>
            {t("landing_cta_btn")}
          </Button>
        </div>
        <div className="text-center text-xs text-zinc-600 mt-12 space-x-4">
          <span>{t("landing_footer")}</span>
          <a href="#faq" data-testid="footer-faq-link" className="text-zinc-500 hover:text-emerald-400 transition-colors underline">{t("footer_faq")}</a>
          <Link to="/terms" data-testid="footer-terms-link" className="text-zinc-500 hover:text-emerald-400 transition-colors underline">{t("footer_terms")}</Link>
          <Link to="/privacy" data-testid="footer-privacy-link" className="text-zinc-500 hover:text-emerald-400 transition-colors underline">{t("footer_privacy")}</Link>
          <Link to="/contact" data-testid="footer-contact-link" className="text-zinc-500 hover:text-emerald-400 transition-colors underline">{t("footer_contact_link")}</Link>
        </div>
        <p data-testid="legal-disclaimer" className="text-center text-[11px] text-zinc-700 mt-4 max-w-2xl mx-auto leading-relaxed">{t("disclaimer_text")}</p>
        <p data-testid="footer-madeby" className="text-center text-[11px] text-zinc-700 mt-3">
          {t("footer_madeby")}{" "}
          <a href="https://innovatoni.com" target="_blank" rel="noopener noreferrer" data-testid="footer-innovatoni-link" className="text-zinc-500 hover:text-emerald-400 transition-colors underline">InnovAtoni</a>
        </p>
      </section>
    </div>
  );
}
